import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Box, Loader, Stack, Text } from "@mantine/core";

import { useCommentsApi } from "./CommentsContext";
import { CommentRow } from "./CommentRow";
import { CommentComposer } from "./CommentComposer";
import type { Comment, CommentTarget, CommentVisibility } from "./types";
import { targetContentType } from "./types";

/**
 * Drop-in <CommentThread> for any entity with a backend Comment row.
 *
 * Wire-up:
 *   1. Mount <CommentsProvider api={…}> once near the root of the host
 *      app (frontend / tenant-frontend).
 *   2. Use <CommentThread target={{app:"crm", model:"tenant", id:42}} />
 *      anywhere it's relevant.
 *
 * The thread fetches its own data via TanStack Query, handles posting /
 * editing / deleting / replying, and re-syncs on every mutation.
 */

export interface CommentThreadProps {
  target: CommentTarget;
  /** Hide the composer (read-only mount). */
  readOnly?: boolean;
  /** Override the composer's default visibility. */
  defaultVisibility?: CommentVisibility;
  /** Header label shown above the thread. Pass empty string to omit. */
  title?: string;
  /** Replaces the composer's locked-visibility caption when the server
   *  reports the target admits only one visibility. Use it to name what
   *  "internal" means here — e.g. "Internal — the member cannot see this". */
  composerLockedLabel?: string;
  /** Called after any successful mutation — useful for parent pages
   *  that want to bump their own "last updated" indicator. */
  onMutated?(): void;
}

interface ThreadNode extends Comment {
  children: ThreadNode[];
}

function buildTree(items: Comment[]): ThreadNode[] {
  // ``items`` arrives ordered ascending by created_at — that's the order
  // the server gives us. Build a depth-aware tree without doing N²
  // lookups: root first, replies attach to their parent_id node.
  const byId: Record<number, ThreadNode> = {};
  const roots: ThreadNode[] = [];
  for (const c of items) {
    byId[c.id] = { ...c, children: [] };
  }
  for (const c of items) {
    const node = byId[c.id];
    if (c.parent_id != null && byId[c.parent_id]) {
      byId[c.parent_id].children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
}

interface RenderHandlers {
  onSave(id: number, body: string): Promise<void>;
  onDelete(id: number, force?: boolean): Promise<void>;
  onReply(parentId: number, parentVisibility: CommentVisibility): void;
  onChangeVisibility(id: number, visibility: CommentVisibility): Promise<void>;
}

const renderRow = (node: ThreadNode, handlers: RenderHandlers): React.ReactNode => (
  <React.Fragment key={node.id}>
    <CommentRow
      comment={node}
      onSave={handlers.onSave}
      onDelete={handlers.onDelete}
      onReply={node.can_reply ? handlers.onReply : undefined}
      onChangeVisibility={node.can_change_visibility ? handlers.onChangeVisibility : undefined}
    />
    {node.children.length > 0 && (
      // Reddit-style nesting: children are wrapped in a left-bordered
      // box so the connector line runs continuously down the parent's
      // gutter. Indent is small (12px) — wide indents waste horizontal
      // real estate at depth 2 on narrow drawers.
      <Box
        ml={6}
        pl={12}
        style={{ borderLeft: "1px solid var(--mantine-color-default-border)" }}
      >
        {node.children.map((child) => renderRow(child, handlers))}
      </Box>
    )}
  </React.Fragment>
);

export const CommentThread: React.FC<CommentThreadProps> = ({
  target,
  readOnly,
  defaultVisibility,
  title = "Comments",
  composerLockedLabel,
  onMutated,
}) => {
  const api = useCommentsApi();
  const queryClient = useQueryClient();
  const contentType = targetContentType(target);
  const objectId = target.id;
  // Cache key keeps the legacy ``"app.model:id"`` shape — easier to
  // read in devtools than two separate fields, and the host app
  // doesn't see it.
  const queryKey = ["comments", `${contentType}:${objectId}`] as const;

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => api.list({ content_type: contentType, object_id: objectId, limit: 200 }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey });
    onMutated?.();
  };

  const createMutation = useMutation({
    mutationFn: (input: {
      body: string;
      visibility: CommentVisibility;
      parentId?: number;
    }) =>
      api.create({
        content_type: contentType,
        object_id: objectId,
        body: input.body,
        visibility: input.visibility,
        parent_id: input.parentId ?? null,
      }),
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: number; body: string }) =>
      api.update(input.id, { body: input.body }),
    onSuccess: invalidate,
  });

  const visibilityMutation = useMutation({
    mutationFn: (input: { id: number; visibility: CommentVisibility }) =>
      api.update(input.id, { visibility: input.visibility }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (input: { id: number; force?: boolean }) =>
      api.remove(input.id, input.force),
    onSuccess: invalidate,
  });

  const [replyTo, setReplyTo] = useState<{
    id: number;
    authorName: string;
    parentVisibility: CommentVisibility;
  } | null>(null);

  const tree = useMemo(() => buildTree(data?.items ?? []), [data?.items]);

  const handlers: RenderHandlers = {
    onSave: async (id: number, body: string) => {
      await updateMutation.mutateAsync({ id, body });
    },
    onDelete: async (id: number, force?: boolean) => {
      await deleteMutation.mutateAsync({ id, force });
    },
    onReply: (parentId, parentVisibility) => {
      const found = data?.items.find((c) => c.id === parentId);
      setReplyTo({
        id: parentId,
        authorName: found?.author_name ?? "comment",
        parentVisibility,
      });
    },
    onChangeVisibility: async (id, visibility) => {
      await visibilityMutation.mutateAsync({ id, visibility });
    },
  };

  if (isLoading) {
    return (
      <Box>
        {title && (
          <Text size="sm" fw={600} mb={6}>
            {title}
          </Text>
        )}
        <Loader size="sm" />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box>
        {title && (
          <Text size="sm" fw={600} mb={6}>
            {title}
          </Text>
        )}
        <Text size="sm" c="red">
          Failed to load comments.
        </Text>
      </Box>
    );
  }

  const items = data?.items ?? [];

  return (
    <Stack gap={8}>
      {title && (
        <Text size="sm" fw={600}>
          {title}{" "}
          <Text component="span" size="xs" c="dimmed">
            {items.length}
          </Text>
        </Text>
      )}
      {items.length === 0 ? (
        <Text size="sm" c="dimmed" fs="italic">
          No comments yet.
        </Text>
      ) : (
        <Stack gap={0}>{tree.map((root) => renderRow(root, handlers))}</Stack>
      )}
      {!readOnly && (
        <Box pt={8} style={{ borderTop: "1px solid var(--mantine-color-default-border)" }}>
          <CommentComposer
            canPostAdmin={data?.can_post_admin ?? false}
            // The server already declares both halves of the capability; the
            // composer used to read only one. ``can_post_public`` is false on
            // targets whose comments are internal by construction, and the
            // composer must not offer a choice the API will overrule.
            canPostPublic={data?.can_post_public ?? true}
            lockedLabel={composerLockedLabel}
            defaultVisibility={defaultVisibility}
            replyTo={replyTo}
            onCancelReply={() => setReplyTo(null)}
            onSubmit={async (body, visibility) => {
              await createMutation.mutateAsync({
                body,
                visibility,
                parentId: replyTo?.id,
              });
            }}
          />
        </Box>
      )}
    </Stack>
  );
};
